import {
  createBaseThemeOptions,
  createUnifiedTheme,
  genPageTheme,
  palettes,
  shapes,
} from '@backstage/theme';

const NTT_RED = '#E4002B';
const NTT_RED_DARK = '#B0001F';
const NTT_RED_LIGHT = '#FF5C7A';
const NTT_NAVY = '#001433';
const NTT_NAVY_MID = '#003087';
const DARK_BG = '#0D1117';
const DARK_CARD = '#161B22';
const DARK_SIDEBAR = '#010409';
const DARK_BORDER = 'rgba(255,255,255,0.08)';

export const nttDarkTheme = createUnifiedTheme({
  ...createBaseThemeOptions({
    palette: {
      ...palettes.light,
      type: 'dark' as any,
      mode: 'dark' as any,
      primary: {
        main: NTT_RED,
        light: NTT_RED_LIGHT,
        dark: NTT_RED_DARK,
        contrastText: '#FFFFFF',
      },
      secondary: {
        main: '#4A90D9',
        light: '#74B0FF',
        dark: '#1D5C99',
        contrastText: '#FFFFFF',
      },
      error: {
        main: '#F44336',
      },
      warning: {
        main: '#FF9800',
      },
      info: {
        main: '#4A90D9',
      },
      success: {
        main: '#4CAF50',
      },
      background: {
        default: DARK_BG,
        paper: DARK_CARD,
      },
      banner: {
        info: '#1565C0',
        error: NTT_RED,
        text: '#FFFFFF',
        link: '#74B0FF',
      },
      errorBackground: '#3B1011',
      warningBackground: '#3B2000',
      infoBackground: '#0D2040',
      navigation: {
        background: DARK_SIDEBAR,
        indicator: NTT_RED,
        color: 'rgba(255,255,255,0.55)',
        selectedColor: '#FFFFFF',
        navItem: {
          hoverBackground: 'rgba(228,0,43,0.2)',
        },
        submenu: {
          background: '#000000',
        },
      },
      text: {
        primary: '#E6EDF3',
        secondary: '#8B949E',
        disabled: '#484F58',
      },
      divider: DARK_BORDER,
    },
  }),
  fontFamily: '"Inter", "Segoe UI", "Roboto", "Helvetica Neue", Arial, sans-serif',
  defaultPageTheme: 'home',
  components: {
    BackstagePage: {
      styleOverrides: {
        root: {
          background: DARK_BG,
        },
      },
    },
    BackstageHeader: {
      styleOverrides: {
        header: () => ({
          width: 'auto',
          background: `linear-gradient(135deg, ${DARK_SIDEBAR} 0%, ${NTT_NAVY} 50%, ${NTT_RED_DARK} 100%)`,
          boxShadow: `0 4px 24px rgba(0,0,0,0.5)`,
          borderBottom: `1px solid ${NTT_RED}30`,
        }),
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: DARK_CARD,
          borderRadius: '12px',
          boxShadow: '0 2px 12px rgba(0,0,0,0.3)',
          border: `1px solid ${DARK_BORDER}`,
          '&:hover': {
            boxShadow: '0 6px 24px rgba(0,0,0,0.5)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: DARK_CARD,
          backgroundImage: 'none',
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        head: {
          backgroundColor: `${DARK_SIDEBAR}!important`,
        },
        root: {
          backgroundColor: `${DARK_CARD}!important`,
          '&:hover': {
            backgroundColor: `${NTT_RED}10!important`,
          },
        },
      },
    },
    BackstageHeaderTabs: {
      styleOverrides: {
        tabsWrapper: {
          backgroundColor: DARK_SIDEBAR,
        },
        defaultTab: {
          textDecoration: 'none!important',
          color: 'rgba(255,255,255,0.5)',
          '&:hover': {
            background: 'rgba(255,255,255,0.06)!important',
            color: '#FFFFFF!important',
          },
        },
      },
    },
    BackstageSelectInputBase: {
      styleOverrides: {
        input: {
          backgroundColor: DARK_CARD,
          border: `1px solid ${DARK_BORDER}`,
          borderRadius: '8px',
          color: '#E6EDF3',
        },
      },
    },
    BackstageEmptyState: {
      styleOverrides: {
        root: {
          backgroundColor: DARK_BG,
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          backgroundColor: NTT_RED,
          height: '3px',
          borderRadius: '3px 3px 0 0',
        },
      },
    },
    BackstageTableHeader: {
      styleOverrides: {
        header: {
          backgroundColor: DARK_SIDEBAR,
          color: '#8B949E',
          fontWeight: 700,
          fontSize: '0.75rem',
          letterSpacing: '0.07em',
          borderBottom: `1px solid ${DARK_BORDER}`,
        },
      },
    },
    BackstageSidebarItem: {
      styleOverrides: {
        root: {
          textDecoration: 'none',
          borderRadius: '8px',
          margin: '1px 8px',
        },
        highlightable: {
          '&:hover': {
            background: 'rgba(228,0,43,0.2)!important',
            color: '#FFFFFF!important',
            borderRadius: '8px',
          },
        },
        selected: {
          background: `linear-gradient(135deg, ${NTT_RED} 0%, ${NTT_RED_DARK} 100%)`,
          borderRadius: '8px',
          color: '#FFFFFF!important',
          boxShadow: `0 2px 12px ${NTT_RED}55`,
        },
        label: {
          fontWeight: 500,
          fontSize: '0.875rem',
        },
      },
    },
  },
  pageTheme: {
    home: genPageTheme({ colors: [DARK_SIDEBAR, NTT_RED], shape: shapes.wave }),
    documentation: genPageTheme({ colors: [NTT_NAVY, '#1A237E'], shape: shapes.wave2 }),
    tool: genPageTheme({ colors: ['#1A237E', NTT_NAVY], shape: shapes.wave }),
    service: genPageTheme({ colors: [NTT_RED_DARK, DARK_SIDEBAR], shape: shapes.wave }),
    website: genPageTheme({ colors: [NTT_NAVY_MID, NTT_NAVY], shape: shapes.wave }),
    library: genPageTheme({ colors: [DARK_SIDEBAR, NTT_RED_DARK], shape: shapes.round }),
    other: genPageTheme({ colors: [NTT_NAVY, DARK_SIDEBAR], shape: shapes.wave }),
    app: genPageTheme({ colors: [NTT_RED_DARK, DARK_SIDEBAR], shape: shapes.wave }),
    apis: genPageTheme({ colors: [NTT_NAVY_MID, NTT_NAVY], shape: shapes.wave }),
  },
});
